import { request } from '@umijs/max';

// 纯获取用户信息请求：只负责发请求
export async function fetchUserInfo() {
    return request<API.BaseResponse<API.CurrentUser>>('/v1/biz/users/current', {
        method: 'GET',
    });
}

// 纯获取用户信息请求：只负责发请求
export async function fetchCardData() {
    return request<API.BaseResponse<API.DailyStatsResponse>>('/v1/biz/users/daily_stats', {
        method: 'GET',
    });
}

// 纯获最新通知请求：只负责发请求，支持动态传分页参数，默认查第1页、每页8条
export async function fetchSysNoticeList(params: { page?: number; page_size?: number } = { page: 1, page_size: 8 }) {
    // 拼接动态参数，避免硬编码在URL中
    return request<API.BaseResponse<API.PageNotiylListResponse<API.NotificationsResponse>>>(
        '/v1/biz/notifications',
        {
            method: 'GET',
            params: {
                page: params.page || 1, // 兜底：默认查第1页
                page_size: params.page_size || 8, // 兜底：默认每页8条
            },
        }
    );
}

// 纯获系统公告请求：只负责发请求，支持动态传分页参数，默认查第1页、每页8条
export async function fetchSystemAnnounList(params: { page?: number; page_size?: number } = { page: 1, page_size: 8 }) {
    // 拼接动态参数，避免硬编码在URL中
    return request<API.BaseResponse<API.PageListResponse<API.AnnouncementsResponse>>>(
        '/v1/biz/announcements',
        {
            method: 'GET',
            params: {
                page: params.page || 1, // 兜底：默认查第1页
                page_size: params.page_size || 8, // 兜底：默认每页8条
            },
        }
    );
}

// 纯获商户订单列表：只负责发请求，支持动态传分页参数，默认查第1页、每页8条
export async function fetchOrdersList(
    params: { page?: number; page_size?: number;[key: string]: any; } = { page: 1, page_size: 8 }
) {
    // 1. 解构参数，分离分页参数和业务搜索参数
    const { page = 1, page_size = 8, ...searchParams } = params;
    // 拼接动态参数，避免硬编码在URL中
    return request<API.BaseResponse<API.PageListResponse<API.OrderDataType>>>(
        '/v1/biz/orders',
        {
            method: 'GET',
            params: {
                page, // 分页：页码（兜底默认1）
                page_size, // 分页：页大小（兜底默认8）
                ...searchParams, // 👇 关键：传递所有搜索参数（如order_no、status、paid_at等）
            },
        }
    );
}

// 纯获商户订单列表：只负责发请求，支持动态传分页参数，默认查第1页、每页8条
export async function fetchSettlementList(
    params: { page?: number; page_size?: number;[key: string]: any; } = { page: 1, page_size: 8 }
) {
    // 1. 解构参数，分离分页参数和业务搜索参数
    const { page = 1, page_size = 8, ...searchParams } = params;
    // 拼接动态参数，避免硬编码在URL中
    return request<API.BaseResponse<API.PageListResponse<API.SettlementResponse>>>(
        '/v1/biz/settlements',
        {
            method: 'GET',
            params: {
                page, // 分页：页码（兜底默认1）
                page_size, // 分页：页大小（兜底默认8）
                ...searchParams, // 👇 关键：传递所有搜索参数（如order_no、status、paid_at等）
            },
        }
    );
}

// 纯获提现商户请求：只负责发请求，支持动态传分页参数，默认查第1页、每页8条
export async function fetchUserAccountList(params: { page?: number; page_size?: number } = { page: 1, page_size: 8 }) {
    // 拼接动态参数，避免硬编码在URL中
    return request<API.BaseResponse<API.PageListResponse<API.UserAccountResponse>>>(
        '/v1/biz/withdraw_accounts',
        {
            method: 'GET',
            params: {
                page: params.page || 1, // 兜底：默认查第1页
                page_size: params.page_size || 8, // 兜底：默认每页8条
            },
        }
    );
}

// 修改提现商户账户状态请求：支持动态传入账户ID和目标状态，参数做合法性兜底校验
export async function updateUserAccountStatus(params: { account_id: number; status: 0 | 1 | 2; }) {
    // 参数合法性校验：避免传入无效参数导致接口报错
    if (!params.account_id || !['0', '1', '2'].includes(String(params.status))) {
        // 抛出错误让调用方捕获，符合前端异步请求错误处理规范
        throw new Error('参数错误：账户ID不能为空，状态值仅支持0（未启用）、1（正常）、2（禁用）');
    }
    // PATCH请求：传递JSON体参数（符合RESTful规范，状态修改用PATCH）
    return request<API.BaseResponse<boolean>>(
        '/v1/biz/withdraw/account/status',  // 路径优化：明确指向“状态修改”资源
        {
            method: 'PATCH',
            data: {  // PATCH请求用data传参（POST/PATCH/PUT用data，GET用params）
                account_id: params.account_id,
                status: params.status,
            },
        }
    );
}

// 修改提现商户账户状态请求：支持动态传入账户ID和目标状态，参数做合法性兜底校验
export async function CreateUserAccount(params: API.CreateUserAccountRequest) {
    // 前置校验：必传参数检查
    if (!params?.account_type || !params?.account_number) {
        return Promise.reject(new Error('账户类型、持有人、账号为必传参数'));
    }
    return request<API.BaseResponse<boolean>>(
        '/v1/biz/withdraw/account',  // 路径优化：明确指向“状态修改”资源
        {
            method: 'POST',
            data: params,
        }
    );
}

// 删除提现商户账户请求：根据账户ID物理删除指定提现账户
// 参数做合法性兜底校验，符合RESTful规范（DELETE方法+路径传资源ID）
export async function deleteUserAccount(params: { account_id: number }) {
    // 1. 前置参数合法性校验（拦截无效请求）
    if (!params || typeof params.account_id !== 'number' || params.account_id <= 0) {
        return Promise.reject(new Error('账户ID不合法，请传入有效的正整数'));
    }
    // DELETE请求：符合RESTful规范（删除资源用DELETE方法）
    return request<API.BaseResponse<boolean>>(`/v1/biz/withdraw/account/${params.account_id}`,
        {
            method: 'DELETE',
        }
    );
}

// 更新提现商户账户请求：参数做合法性兜底校验
export async function updateUserAccount(params: {
    account_id: number; alias_name?: string; real_name?: string; account_number?: string;
    bank_name?: string; network_type?: string; account_type?: string;
}) {
    // 1. 前置参数校验
    if (!params || typeof params.account_id !== 'number' || params.account_id <= 0) {
        return Promise.reject(new Error('账户ID不合法，请传入有效的正整数'));
    }

    // 2. 构建请求参数（仅传递非空字段）
    const requestData = {} as any;
    if (params.alias_name) requestData.alias_name = params.alias_name;
    if (params.real_name) requestData.real_name = params.real_name;
    if (params.account_number) requestData.account_number = params.account_number;
    if (params.bank_name) requestData.bank_name = params.bank_name;
    if (params.network_type) requestData.network_type = params.network_type;
    if (params.account_type) requestData.account_type = params.account_type;

    // 3. PATCH请求（符合RESTful规范：部分更新资源）
    return request<API.BaseResponse<boolean>>(`/v1/biz/withdraw/account/${params.account_id}`,
        {
            method: 'PATCH',
            data: requestData,
        }
    );
}

/** * 根据类型获取提现账户列表 
 * @param type 账户类型: 'alipay' | 'bank' | 'usdt'
 */
export async function fetchAccountsByType(type: string) {
    return request<API.BaseResponse<API.UserAccountResponse[]>>(
        '/v1/biz/withdraw_accounts_by_type',
        {
            method: 'GET',
            params: { type },
        }
    );
}

/** * 提交提现申请 
 * @param data 提现表单数据
 */
export async function submitWithdrawRequest(data: {
    withdraw_mode: string;
    amount: number;
    account_id: number;
    password?: string;
}) {
    return request<API.BaseResponse<any>>('/v1/biz/withdraw/apply', {
        method: 'POST',
        data,
    });
}

/**
 * 提现申请订单列表
 */
export async function fetchWithdrawOrdersList(
    params: { page?: number; page_size?: number;[key: string]: any; } = { page: 1, page_size: 8 }
) {
    // 1. 解构参数，分离分页参数和业务搜索参数
    const { page = 1, page_size = 8, ...searchParams } = params;
    // 拼接动态参数，避免硬编码在URL中
    return request<API.BaseResponse<API.PageListResponse<API.WithdrawOrderResponse>>>(
        '/v1/biz/withdraw/orders',
        {
            method: 'GET',
            params: {
                page, // 分页：页码（兜底默认1）
                page_size, // 分页：页大小（兜底默认8）
                ...searchParams, // 👇 关键：传递所有搜索参数（如order_no、status、paid_at等）
            },
        }
    );
}

/**
 * 标记通知为已读
 * @param params { id?: number } - 如果不传 id，后端逻辑将执行“全部已读”
 */
export async function updateNoticeStatus(params: { id?: number }) {
    return request<API.BaseResponse>('/v1/biz/notifications/read', {
        method: 'POST',
        data: params,
    });
}

/**
 * 修改登录密码
 * @param data { old_password: string, new_password: string }
 */
export async function updateLoginPassword(data: API.UpdatePasswordParams) {
    return request<API.BaseResponse>('/v1/biz/users/password/login', {
        method: 'POST',
        data,
    });
}

/**
 * 修改/设置支付密码
 * @param data { old_password: string, new_pay_pwd: string }
 * @description 注意：此处 old_password 传的是登录密码用于二次验证
 */
export async function updatePayPassword(data: API.UpdatePayPasswordParams) {
    return request<API.BaseResponse>('/v1/biz/users/password/pay', {
        method: 'POST',
        data,
    });
}